import ChartLine, { TooltipParams } from '@anm/components/charts/ChartLine';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import useHLSVideo from '@anm/hooks/useHLSVideo';
import { useCallback, FC } from 'react';

import { useNoDataMessage } from '../hooks';
import { AnalyticsTabProps } from '../types';

import {
  EngagementChartWrapper,
  ImageOverlay,
  ImagePlaceholder,
  InfoMessage,
  IMAGE_TOP_OFFSET,
  LoaderWrapper,
  Preview,
  PreviewWrapper,
  Video
} from './Wrapper';
import { drawEngagementChartTooltip } from './helpers';
import { useCalcNotRenderedPreviewHeight, useChartData } from './hooks';
import { VideoPreviewProps } from './types';

type PreviewBlockProps = AnalyticsTabProps & VideoPreviewProps & { isDataExist: boolean };

const PreviewBlock: FC<PreviewBlockProps> = ({
  preview,
  videoUrl,
  videoRef,
  imageRef,
  chartSizes,
  isDataExist,
  setIsImageLoaded,
  ...restProps
}) => {
  const chartMarginRight = isMobileOrIPad() ? 1 : 45;
  const [placeholderRef, placeholderPreviewHeight] = useCalcNotRenderedPreviewHeight();

  const [chartData, isPending, averageViews, projectDuration, isProjectEverRender] = useChartData();

  const [canShowMessage, renderMessage] = useNoDataMessage(restProps);

  const handleDrawTooltip = useCallback(
    (params: TooltipParams) => drawEngagementChartTooltip(params)(videoRef, projectDuration),
    [videoRef, projectDuration]
  );

  const showChart = !!averageViews && !isPending;
  const isInfoMessage = !showChart && isProjectEverRender && canShowMessage;

  const safeChartData = chartData.slice(0, projectDuration);

  useHLSVideo({ videoRef, hlsVideo: videoUrl });

  return (
    <PreviewWrapper minHeigh={chartSizes.height}>
      {isDataExist && videoUrl && <Video src={videoUrl} ref={videoRef} />}
      {preview && (
        <Preview ref={imageRef} src={preview} isAbsolute={!!videoUrl} alt="project preview" onLoad={setIsImageLoaded} />
      )}
      {!preview && <ImagePlaceholder ref={placeholderRef} height={placeholderPreviewHeight} />}
      {preview && <ImageOverlay height={chartSizes.height + IMAGE_TOP_OFFSET} />}
      {isInfoMessage && <InfoMessage>{renderMessage(true)}</InfoMessage>}
      {isPending && <LoaderWrapper size="medium" color="blue" />}
      {showChart && (
        <EngagementChartWrapper>
          <ChartLine
            {...chartSizes}
            data={safeChartData}
            margin={{ right: chartMarginRight }}
            drawCustomTooltip={handleDrawTooltip}
          />
        </EngagementChartWrapper>
      )}
    </PreviewWrapper>
  );
};

export default PreviewBlock;
